import * as React from 'react';
import { Box, Paper, Typography, Skeleton } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { formatQuantity } from '../../../utils';

/**
 * Metric card component for displaying KPIs
 */
function MetricCard({
  icon: Icon,
  label,
  value,
  changePercent,
  changeType,
  loading,
  format = 'number',
  iconColor = 'secondary.main',
  iconBgColor = 'rgba(49, 130, 206, 0.1)',
}) {
  const formatValue = (val) => {
    if (val === null || val === undefined) return '-';

    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('fi-FI', {
          style: 'currency',
          currency: 'EUR',
        }).format(val);
      case 'percent':
        return `${val.toFixed(1)} %`;
      case 'decimal':
        return new Intl.NumberFormat('fi-FI', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(val);
      case 'hours':
        // Two decimals: one rounds every quarter hour (8,25 h showed as 8,3).
        return formatQuantity(val);
      case 'text':
        return val;
      default:
        return new Intl.NumberFormat('fi-FI').format(val);
    }
  };

  const showTrend = changePercent !== null && changePercent !== undefined;
  const isPositive = changeType === 'increase' || (changePercent > 0 && changeType !== 'decrease');

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        {Icon && (
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '10px',
              bgcolor: iconBgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: iconColor,
            }}
          >
            <Icon />
          </Box>
        )}
        {showTrend && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '13px',
              fontWeight: 500,
              color: isPositive ? 'success.main' : 'error.main',
            }}
          >
            {isPositive ? (
              <TrendingUpIcon fontSize="small" />
            ) : (
              <TrendingDownIcon fontSize="small" />
            )}
            {Math.abs(changePercent).toFixed(1)}%
          </Box>
        )}
      </Box>

      {loading ? (
        <Skeleton variant="text" width={100} height={40} />
      ) : (
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 0.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {formatValue(value)}
        </Typography>
      )}

      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}

export default MetricCard;
